import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { MAX_RULES, MAX_RULE_TEXT_LENGTH } from 'skipify-types';

import { ConfigSection } from '../../../common';
import Switch from '../../../common/Switch';
import { EllaRule } from '../../../store/modules/merchant/types';
import { useEllaDraft } from '../EllaDraftContext';

const FORM_MAX_WIDTH = 501;
const WORKFLOWS_DESCRIPTION_MAX_WIDTH = 700;

const emptyRule = (): EllaRule => ({
  name: '',
  text: '',
  enabled: true,
});

const ruleLabel = (rule: EllaRule): string => {
  const name = (rule.name ?? '').trim();
  if (name) {
    return name;
  }
  const text = rule.text.trim();
  return text || 'Untitled workflow';
};

const isRuleBlank = (rule?: EllaRule) => !(rule?.name ?? '').trim() && !(rule?.text ?? '').trim();

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontFamily: 'Poppins, sans-serif',
    fontSize: 14,
    lineHeight: '18px',
    padding: 0,
    '& fieldset': {
      borderColor: 'primary.main',
    },
    '&:hover fieldset': {
      borderColor: 'primary.main',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.dark',
      borderWidth: 1,
    },
  },
  // Figma text field/input: px 16, py 12
  '& .MuiOutlinedInput-input': {
    px: '16px',
    py: '12px',
    height: 'auto',
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-inputMultiline': {
    px: '16px',
    py: '12px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'primary.main',
    opacity: 1,
  },
};

const addRuleButtonSx = {
  height: 48,
  borderRadius: '51px',
  textTransform: 'none' as const,
  fontFamily: 'Poppins, sans-serif',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.4,
  boxShadow: 'none',
  width: '100%',
  color: 'primary.dark',
  bgcolor: 'background.paper',
  borderColor: 'primary.light',
  '&:hover': {
    bgcolor: 'background.paper',
    borderColor: 'primary.main',
    boxShadow: 'none',
  },
};

export const WorkflowsForm = (): JSX.Element => {
  const { draft, readOnly, isDirty, updateDraft } = useEllaDraft();
  const rules = draft.rules ?? [];
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const wasDirtyRef = useRef(isDirty);

  const atLimit = rules.length >= MAX_RULES;

  // After shared SaveBar save/cancel, collapse the open form into the list.
  useEffect(() => {
    if (wasDirtyRef.current && !isDirty) {
      setEditingIndex(null);
    }
    wasDirtyRef.current = isDirty;
  }, [isDirty]);

  const setRules = (next: EllaRule[]) => {
    updateDraft((prev) => ({
      ...prev,
      rules: next,
    }));
  };

  const updateRuleAt = (index: number, patch: Partial<EllaRule>) => {
    setRules(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  const onAddRule = () => {
    if (readOnly || atLimit) {
      return;
    }

    // If a blank rule already exists, open its form instead of adding another.
    const blankIndex = rules.findIndex((rule) => isRuleBlank(rule));
    if (blankIndex >= 0) {
      setEditingIndex(blankIndex);
      return;
    }

    // Newest rules stay at the top; existing rules shift down.
    updateDraft((prev) => ({
      ...prev,
      rules: [emptyRule(), ...(prev.rules ?? [])],
    }));
    setEditingIndex(0);
  };

  const onEditRule = (index: number) => {
    if (readOnly) {
      return;
    }
    setEditingIndex(index);
  };

  const onCollapseRule = (index: number) => {
    if (isRuleBlank(rules[index])) {
      onDeleteRule(index);
      return;
    }
    setEditingIndex(null);
  };

  const onDeleteRule = (index: number) => {
    if (readOnly) {
      return;
    }
    setRules(rules.filter((_, i) => i !== index));
    setEditingIndex((current) => {
      if (current === null) {
        return null;
      }
      if (current === index) {
        return null;
      }
      if (current > index) {
        return current - 1;
      }
      return current;
    });
  };

  return (
    <Box
      component="form"
      id="ella-workflows-form"
      data-testid="ella-workflows-form"
      onSubmit={(e) => e.preventDefault()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <ConfigSection
        title="Behavioral Workflows"
        description={`Manage the workflows the shopping agent applies in shopper conversations. Add knowledge they can't infer on their own — quirks, current offers, what to ask — written like guidance for a great in-store associate. The shopping agent follows each workflow when it's relevant (e.g. “always mention free 30-day returns at checkout”). Toggle a workflow off to pause it without deleting it. Max ${MAX_RULES} workflows.`}
        marginBottom={2}
        sx={{ maxWidth: WORKFLOWS_DESCRIPTION_MAX_WIDTH }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxWidth: FORM_MAX_WIDTH,
          }}
        >
          {!atLimit && (
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              fullWidth
              disabled={readOnly}
              onClick={onAddRule}
              sx={addRuleButtonSx}
              data-testid="ella-add-rule"
            >
              Add new workflow
            </Button>
          )}

          {atLimit && (
            <Typography color="text.secondary" variant="body2" sx={{ m: 0 }}>
              Maximum of {MAX_RULES} workflows reached.
            </Typography>
          )}

          {rules.map((rule, index) => {
            const isEditing = editingIndex === index;

            // Blank in-progress rules only render as the open form.
            if (!isEditing && isRuleBlank(rule)) {
              return null;
            }

            if (isEditing) {
              const nameValue = rule.name ?? '';
              const textValue = rule.text ?? '';
              const nameError = nameValue.length > 0 && nameValue.trim().length === 0;
              const textError = textValue.length > MAX_RULE_TEXT_LENGTH;

              return (
                <Box
                  key={`rule-${index}`}
                  data-testid={`ella-rule-form-${index}`}
                  sx={{
                    // Figma 776:3692 — card padding 12px, radius 8px, border #C4C4C4
                    p: '12px',
                    border: '1px solid',
                    borderColor: 'secondary.main',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    bgcolor: '#fff',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        m: 0,
                        color: 'text.secondary',
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: '16px',
                      }}
                    >
                      Workflow {index + 1}
                    </Typography>
                    <IconButton
                      aria-label={`Collapse workflow ${index + 1}`}
                      onClick={() => onCollapseRule(index)}
                      disabled={readOnly}
                      size="small"
                    >
                      <ExpandLessIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <TextField
                      fullWidth
                      placeholder="Workflow name"
                      value={nameValue}
                      onChange={(e) => updateRuleAt(index, { name: e.target.value })}
                      disabled={readOnly}
                      error={nameError}
                      helperText={nameError ? 'Workflow name is required' : undefined}
                      inputProps={{
                        'aria-label': 'Workflow name',
                      }}
                      sx={textFieldSx}
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        placeholder="Workflow description"
                        value={textValue}
                        onChange={(e) => updateRuleAt(index, { text: e.target.value })}
                        disabled={readOnly}
                        error={textError}
                        inputProps={{
                          'aria-label': 'Workflow description',
                          maxLength: MAX_RULE_TEXT_LENGTH,
                        }}
                        sx={textFieldSx}
                      />

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                        }}
                      >
                        <Switch
                          isChecked={!!rule.enabled}
                          onToggle={(checked) => updateRuleAt(index, { enabled: checked })}
                          isDisabled={readOnly}
                          aria-label={`Enable workflow ${index + 1}`}
                        />
                        <Typography
                          sx={{
                            m: 0,
                            color: textError ? 'error.main' : 'text.secondary',
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: 10,
                            fontWeight: 400,
                            lineHeight: '16px',
                          }}
                        >
                          {`${textValue.length}/${MAX_RULE_TEXT_LENGTH} characters`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            }

            return (
              <Box
                key={`rule-${index}`}
                data-testid={`ella-rule-row-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  border: '1px solid',
                  borderColor: 'secondary.main',
                  borderRadius: '8px',
                  bgcolor: '#fff',
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    m: 0,
                    color: rule.enabled ? 'primary.dark' : 'text.secondary',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ruleLabel(rule)}
                </Typography>

                <IconButton
                  aria-label={`Edit ${ruleLabel(rule)}`}
                  onClick={() => onEditRule(index)}
                  disabled={readOnly}
                  size="small"
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>

                <IconButton
                  aria-label={`Delete ${ruleLabel(rule)}`}
                  onClick={() => onDeleteRule(index)}
                  disabled={readOnly}
                  size="small"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </ConfigSection>
    </Box>
  );
};
